import type { ReactNode } from "react";
import type { AnalyticsMetrics, AnalyticsPaymentTotal, AnalyticsSummary } from "@/api/client";
import { ScrollArea } from "@/components/ScrollArea";
import { formatAmount } from "@/features/payments/amount";

type Group = "day" | "week" | "month";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function periodLabel(start: string, group: Group): string {
  const [y, m, d] = start.split("-").map(Number) as [number, number, number];
  const month = MONTHS[m - 1];
  if (group === "month") return `${month} ${y}`;
  return `${group === "week" ? "w/c " : ""}${d} ${month} ${y}`;
}

const TH = "px-4 py-3 font-medium";
const TD = "px-4 py-3 tabular-nums";

export function PaidCell({ payments }: { payments: AnalyticsPaymentTotal[] }) {
  if (payments.length === 0) return <>—</>;
  return (
    <>
      {payments.map((p) => (
        <div key={p.currency}>{formatAmount(p.induction_minor + p.reissue_minor, p.currency)}</div>
      ))}
    </>
  );
}

function MetricCells({ m }: { m: AnalyticsMetrics }) {
  return (
    <>
      <td className={TD}>{m.issued}</td>
      <td className={TD}>{m.reissued}</td>
      <td className={TD}>{m.revoked}</td>
      <td className={TD}>{m.emails_sent}</td>
      <td className={TD}>{m.emails_failed}</td>
      <td className={TD}>{m.printed}</td>
      <td className={TD}>
        <PaidCell payments={m.payments} />
      </td>
    </>
  );
}

const METRIC_HEADS = ["Issued", "Re-issued", "Revoked", "Emails sent", "Emails failed", "Printed", "Paid"];

function Table({ first, label, children }: { first: string; label: string; children: ReactNode }) {
  return (
    <ScrollArea label={label}>
      <table aria-label={label} className="w-full min-w-[760px] text-left text-sm">
        <thead className="border-b border-zinc-200 bg-zinc-50 text-xs uppercase text-zinc-500">
          <tr>
            {[first, ...METRIC_HEADS].map((h) => (
              <th key={h} scope="col" className={TH}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">{children}</tbody>
      </table>
    </ScrollArea>
  );
}

export function TotalsRow({ totals }: { totals: AnalyticsMetrics }) {
  const items: [string, ReactNode][] = [
    ["Issued", totals.issued],
    ["Re-issued", totals.reissued],
    ["Revoked", totals.revoked],
    ["Emails sent", totals.emails_sent],
    ["Emails failed", totals.emails_failed],
    ["Printed", totals.printed],
    ["Paid", <PaidCell key="paid" payments={totals.payments} />],
  ];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
      {items.map(([label, value]) => (
        <div key={label} className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2">
          <div className="text-xs text-zinc-500">{label}</div>
          <div className="text-lg font-semibold text-zinc-950">{value}</div>
        </div>
      ))}
    </div>
  );
}

export function PeriodTable({ buckets, group }: { buckets: AnalyticsSummary["buckets"]; group: Group }) {
  return (
    <div>
      <Table first="Period" label="By period">
        {buckets.map((b) => (
          <tr key={b.start} className="hover:bg-zinc-50">
            <td className={`${TD} font-medium text-zinc-950`}>{periodLabel(b.start, group)}</td>
            <MetricCells m={b} />
          </tr>
        ))}
      </Table>
    </div>
  );
}

export function ProgramTable({ rows }: { rows: AnalyticsSummary["by_program"] }) {
  return (
    <Table first="Program" label="By program">
      {rows.map((r) => (
        <tr key={r.program_id ?? "unassigned"} className="hover:bg-zinc-50">
          <td className={`${TD} font-medium text-zinc-950`}>{r.program_id === null ? "Unassigned" : r.program_name}</td>
          <MetricCells m={r} />
        </tr>
      ))}
    </Table>
  );
}
